// Physics → Section 12: The Earth and Beyond (KS3, sourced from
// the Grade9Ready science PDF). 3 topics as specified by the user.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'physics/earth-and-beyond';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 12.1  GRAVITY
// =====================================================================
const GRAVITY_TOPIC = {
  id: 'phys-12.1',
  title: 'Gravity',
  slug: 'gravity',
  pageRef: 'Section 12 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What gravity is',
        body:
          'Gravity is a force of ATTRACTION between any two objects with mass. The bigger the masses, and the closer together they are, the stronger the pull. On Earth, gravity is what gives objects WEIGHT — the force that pulls you towards the centre of the planet. The Earth pulls you down; you (much more weakly) pull the Earth up by the same amount.',
        check: {
          question: 'Gravity is a force of…',
          options: ['Repulsion between charged objects', 'Attraction between any two masses', 'Friction between surfaces', 'Pressure inside fluids'],
          answer: 'Attraction between any two masses',
          explainer: 'All masses attract — bigger and closer = stronger pull.',
        },
      },
      {
        heading: 'Mass vs weight',
        body:
          'MASS is the amount of stuff in an object. It\u2019s measured in kilograms (kg) and stays the SAME everywhere. WEIGHT is the FORCE of gravity on that mass. It\u2019s measured in newtons (N) and CHANGES depending on where you are. Equation: W = m × g, where g is the gravitational field strength. On Earth g ≈ 10 N/kg; on the Moon g ≈ 1.6 N/kg; in deep space, almost zero.',
        check: {
          question: 'A 50 kg student stands on the Moon (g = 1.6 N/kg). Their weight is…',
          options: ['50 N', '500 N', '80 N', '0 N'],
          answer: '80 N',
          explainer: 'W = m × g = 50 × 1.6 = 80 N. (On Earth they\u2019d weigh 500 N.)',
        },
      },
      {
        heading: 'Gravity in the solar system',
        body:
          'Gravity holds the solar system together. The Sun\u2019s huge mass pulls every planet into a (nearly) circular ORBIT. The Moon orbits Earth for the same reason — it\u2019s "falling around" the planet but moving sideways fast enough to never hit it. Astronauts on the Space Station appear to FLOAT not because there\u2019s no gravity, but because they\u2019re in CONTINUOUS free-fall around Earth (along with the station).',
        check: {
          question: 'Why do astronauts on the Space Station float?',
          options: ['No gravity in space', 'They are continuously falling around Earth', 'Air pressure lifts them', 'They are weightless objects'],
          answer: 'They are continuously falling around Earth',
          explainer: 'Gravity at the ISS is ~90% of surface — they\u2019re in free-fall, not gravity-free.',
        },
      },
    ],
    keyPoints: [
      'Gravity = attractive force between any two masses',
      'Bigger masses + closer = stronger pull',
      'Mass (kg) is constant; weight (N) depends on g',
      'W = m × g; Earth g ≈ 10 N/kg, Moon g ≈ 1.6 N/kg',
      'Orbits exist because gravity continually pulls planets/moons sideways',
    ],
    workedExamples: [
      {
        problem: 'A 4 kg bag of flour is taken to Mars (g = 3.7 N/kg). Calculate (a) its mass on Mars and (b) its weight on Mars.',
        steps: [
          { step: 1, instruction: 'Mass on Mars', working: 'Mass is constant → still 4 kg' },
          { step: 2, instruction: 'Weight equation', working: 'W = m × g' },
          { step: 3, instruction: 'Substitute', working: 'W = 4 × 3.7 = 14.8 N' },
        ],
        answer: 'Mass = 4 kg; Weight = 14.8 N.',
        commonMistake: 'Saying mass changes between planets. Mass is constant; weight is what changes.',
      },
    ],
    whyItWorks:
      'Gravity is the most "always on" force in the universe — it never switches off and acts at any distance. It built the planets out of dust, holds the solar system together, and keeps your feet on the ground. Even light bends when it passes near very massive objects.',
  },
  glossary: [
    { term: 'Gravity', definition: 'A force of attraction between objects that have mass.' },
    { term: 'Mass', definition: 'The amount of matter in an object; unit kilograms (kg).' },
    { term: 'Weight', definition: 'The force of gravity on a mass; unit newtons (N).' },
    { term: 'Gravitational field strength (g)', definition: 'The force of gravity per kilogram; unit N/kg.' },
    { term: 'Orbit', definition: 'The (almost) circular path of one body around another due to gravity.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the equation linking weight, mass and gravitational field strength. (b) Calculate the weight of a 60 kg astronaut on Earth (g = 10 N/kg) and on the Moon (g = 1.6 N/kg). (c) Explain why their MASS is the same in both places. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'W = m × g',
      'On Earth: W = 60 × 10 = 600 N',
      'On Moon: W = 60 × 1.6 = 96 N',
      'Mass is the amount of matter in the astronaut',
      'It does not change with location; only the gravitational field strength changes',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-12.1.w1', type: 'choice', question: 'Mass is measured in…', options: ['Newtons', 'Kilograms', 'Metres', 'Joules'], answer: 'Kilograms', hint1: '9 letters', hint2: 'Symbol kg', hint3: 'Scale unit' },
      { id: 'phys-12.1.w2', type: 'choice', question: 'Weight is measured in…', options: ['Kilograms', 'Newtons', 'Watts', 'Litres'], answer: 'Newtons', hint1: '7 letters', hint2: 'Symbol N', hint3: 'Force unit' },
      { id: 'phys-12.1.w3', type: 'number', question: 'm = 5 kg, g = 10 N/kg. Weight (N)?', answer: '50', hint1: 'm × g', hint2: 'Two digits', hint3: 'Five-zero' },
    ],
    level1: [
      { id: 'phys-12.1.1', type: 'choice', question: 'Which has more gravity at its surface?', options: ['Moon', 'Earth', 'Same', 'Neither'], answer: 'Earth', hint1: 'Bigger mass', hint2: 'Stronger pull', hint3: '5 letters' },
      { id: 'phys-12.1.2', type: 'text', question: 'Gravitational field strength on Earth is about ___ N/kg.', answer: '10', hint1: 'Two digits', hint2: 'Ten', hint3: 'Used for W = mg' },
    ],
    level2: [
      { id: 'phys-12.1.3', type: 'number', question: 'Astronaut mass 70 kg on Moon (g = 1.6). Weight (N)?', answer: '112', hint1: '70 × 1.6', hint2: 'Three digits', hint3: 'About 1/6 of Earth' },
      { id: 'phys-12.1.4', type: 'choice', question: 'Take a 5 kg rock from Earth to Mars. Mass on Mars?', options: ['Less', 'Same', 'Bigger', 'Zero'], answer: 'Same', hint1: 'Mass is constant', hint2: '5 kg always', hint3: 'Only weight changes' },
    ],
    level3: [
      { id: 'phys-12.1.5', type: 'choice', question: 'Why do astronauts on the ISS appear weightless?', options: ['No gravity in space', 'They are in continuous free-fall around Earth', 'They wear special suits', 'They are far from any planet'], answer: 'They are in continuous free-fall around Earth', hint1: 'Falling sideways too fast to hit', hint2: 'Gravity ~90% of surface', hint3: 'Linked to orbit' },
      { id: 'phys-12.1.6', type: 'text', question: 'The path of a planet around the Sun is called an ___ .', answer: 'orbit', hint1: '5 letters', hint2: 'Curved path', hint3: 'Starts with o' },
    ],
  },
};

// =====================================================================
// 12.2  THE SUN
// =====================================================================
const SUN_TOPIC = {
  id: 'phys-12.2',
  title: 'The Sun',
  slug: 'the-sun',
  pageRef: 'Section 12 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'A medium-sized star',
        body:
          'The Sun is a STAR — a huge ball of mostly hydrogen and helium gas, held together by its own gravity. It\u2019s about 1.4 million km across (109 Earths fit across its diameter) and 150 million km away. Compared with other stars it\u2019s actually quite ordinary — medium sized, medium hot, middle-aged (about 4.6 billion years old, with another ~5 billion to go).',
        check: {
          question: 'The Sun is a…',
          options: ['Planet', 'Moon', 'Star', 'Comet'],
          answer: 'Star',
          explainer: 'Stars produce their own light by nuclear fusion. The Sun is our local star.',
        },
      },
      {
        heading: 'Where the energy comes from',
        body:
          'Deep in the Sun\u2019s core, the temperature is about 15 MILLION °C and the pressure is enormous. Hydrogen nuclei are squeezed so hard they FUSE into helium nuclei, releasing massive amounts of energy as light and heat. This process is called NUCLEAR FUSION. The energy escapes outward and eventually reaches Earth as sunlight — the source of nearly all our energy on the planet (food, weather, fossil fuels).',
        check: {
          question: 'How does the Sun produce its energy?',
          options: ['Burning coal', 'Burning hydrogen with oxygen', 'Nuclear fusion of hydrogen into helium', 'Friction'],
          answer: 'Nuclear fusion of hydrogen into helium',
          explainer: 'Stars run on fusion — combining small nuclei into bigger ones, releasing energy.',
        },
      },
      {
        heading: 'Why life on Earth depends on the Sun',
        body:
          'Sunlight powers nearly all life: PLANTS use it for photosynthesis (making sugars from CO₂ and water); animals eat plants (or other animals) for that energy. Sunlight drives the WEATHER (heating air → wind, evaporating water → rain). Even fossil fuels (coal, oil, gas) are stored sunlight — captured millions of years ago by plants and trapped underground. Without the Sun, Earth would freeze in days.',
        check: {
          question: 'Coal, oil and gas are sometimes called "stored sunlight" because…',
          options: ['They look yellow', 'They were formed from plants that captured sunlight long ago', 'They glow', 'They contain hydrogen'],
          answer: 'They were formed from plants that captured sunlight long ago',
          explainer: 'All fossil fuels are buried biomass — sunlight from millions of years ago.',
        },
      },
    ],
    keyPoints: [
      'Sun = a medium-sized star, mostly H + He, ~150 million km away',
      'Diameter ~1.4 million km (109 Earths across)',
      'Nuclear FUSION in the core: H → He releases huge energy',
      'Core temperature ~15 million °C',
      'Powers life on Earth: photosynthesis, weather, fossil fuels',
    ],
    workedExamples: [
      {
        problem: 'Light takes about 8 minutes to reach Earth from the Sun. Use this to estimate the Sun-Earth distance. (Speed of light = 3 × 10⁸ m/s.)',
        steps: [
          { step: 1, instruction: 'Convert 8 min to seconds', working: '8 × 60 = 480 s' },
          { step: 2, instruction: 'Distance = speed × time', working: '3 × 10⁸ × 480' },
          { step: 3, instruction: 'Calculate', working: '= 1.44 × 10¹¹ m' },
          { step: 4, instruction: 'Convert to km', working: '= 1.44 × 10⁸ km ≈ 144 million km' },
        ],
        answer: 'About 150 million km (the textbook value).',
        commonMistake: 'Using minutes directly without converting to seconds. Always match the units of speed.',
      },
    ],
    whyItWorks:
      'The Sun is a continuous, gigantic hydrogen bomb held together by gravity. Every second, it converts about 600 million tonnes of hydrogen into helium. Even after 4.6 billion years it\u2019s only halfway through its hydrogen — that\u2019s how vast a star\u2019s fuel store is.',
  },
  glossary: [
    { term: 'Star', definition: 'A massive ball of gas held together by gravity, generating energy by nuclear fusion.' },
    { term: 'Nuclear fusion', definition: 'The process where small nuclei (e.g. hydrogen) join to form a bigger nucleus (helium), releasing energy.' },
    { term: 'Solar system', definition: 'The Sun and everything orbiting it — planets, moons, asteroids, comets.' },
    { term: 'Photosynthesis', definition: 'The process plants use to convert sunlight + CO₂ + water into sugars and oxygen.' },
  ],
  examQuestion: {
    prompt:
      '(a) Describe what the Sun is. (b) State the name of the process that produces the Sun\u2019s energy. (c) Explain why life on Earth depends on the Sun. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'A star — a massive ball of hot gas (mostly hydrogen and helium)',
      'held together by its own gravity',
      'Nuclear fusion (hydrogen into helium)',
      'Plants need sunlight for photosynthesis (foundation of food chains)',
      'Sunlight drives the weather / and is the original source of fossil fuels',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-12.2.w1', type: 'text', question: 'The Sun is mostly made of hydrogen and ___ .', answer: 'helium', hint1: '6 letters', hint2: 'Element symbol He', hint3: 'Starts with h' },
      { id: 'phys-12.2.w2', type: 'choice', question: 'The Sun is a…', options: ['Planet', 'Moon', 'Star', 'Galaxy'], answer: 'Star', hint1: '4 letters', hint2: 'Generates own light', hint3: 'Closest one to Earth' },
      { id: 'phys-12.2.w3', type: 'choice', question: 'The Sun produces energy by…', options: ['Burning fuel', 'Nuclear fusion', 'Reflection', 'Friction'], answer: 'Nuclear fusion', hint1: 'Two words', hint2: 'H → He', hint3: 'Same as all stars' },
    ],
    level1: [
      { id: 'phys-12.2.1', type: 'text', question: 'The Sun is about 150 million ___ from Earth.', answer: 'km', hint1: '2 letters', hint2: 'Unit of distance', hint3: 'Kilometres' },
      { id: 'phys-12.2.2', type: 'choice', question: 'Light from the Sun takes about ___ to reach Earth.', options: ['8 seconds', '8 minutes', '8 hours', '8 days'], answer: '8 minutes', hint1: '~500 s', hint2: 'A few minutes', hint3: 'Mid-range' },
    ],
    level2: [
      { id: 'phys-12.2.3', type: 'choice', question: 'The Sun\u2019s core temperature is roughly…', options: ['1,500 °C', '15,000 °C', '150,000 °C', '15,000,000 °C'], answer: '15,000,000 °C', hint1: 'Many millions', hint2: '15 million', hint3: 'Allows fusion to happen' },
      { id: 'phys-12.2.4', type: 'choice', question: 'Plants depend on the Sun for…', options: ['Gravity', 'Photosynthesis', 'Reproduction', 'Respiration'], answer: 'Photosynthesis', hint1: 'Sugar-making', hint2: 'Uses light energy', hint3: '14 letters' },
    ],
    level3: [
      { id: 'phys-12.2.5', type: 'choice', question: 'About how old is the Sun?', options: ['4.6 thousand years', '4.6 million years', '4.6 billion years', '4.6 trillion years'], answer: '4.6 billion years', hint1: 'Same age as the Earth', hint2: 'Halfway through life', hint3: 'Very large' },
      { id: 'phys-12.2.6', type: 'text', question: 'Coal, oil and gas are sometimes called "stored ___ ".', answer: 'sunlight', hint1: '8 letters', hint2: 'Energy from the Sun', hint3: 'One word' },
    ],
  },
};

// =====================================================================
// 12.3  DAY AND NIGHT AND THE FOUR SEASONS
// =====================================================================
const DAY_NIGHT_SEASONS_TOPIC = {
  id: 'phys-12.3',
  title: 'Day and Night and the Four Seasons',
  slug: 'day-and-night-and-the-four-seasons',
  pageRef: 'Section 12 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Day and night',
        body:
          'Earth spins on its AXIS once every 24 hours. As it does, the side facing the Sun has DAY (sunlight); the side facing away has NIGHT (darkness). Earth\u2019s axis runs roughly through the North and South poles. The spin is from west to east, so the Sun appears to RISE in the east and SET in the west — even though it\u2019s actually the Earth that\u2019s moving.',
        check: {
          question: 'Day and night happen because…',
          options: ['The Sun moves around Earth', 'Earth spins on its axis', 'Earth orbits the Sun', 'Clouds block sunlight'],
          answer: 'Earth spins on its axis',
          explainer: '24-hour spin → half lit (day), half dark (night).',
        },
      },
      {
        heading: 'Why we have a year',
        body:
          'While Earth spins, it also ORBITS the Sun. One full lap = 365.25 days = ONE YEAR. (We add an extra day to February every 4 years — a "leap year" — to make up the quarter-day.) The orbit is nearly circular, so Earth-to-Sun distance barely changes through the year — which means the seasons aren\u2019t caused by getting closer or further from the Sun (a common misconception).',
        check: {
          question: 'How long is one Earth orbit around the Sun?',
          options: ['24 hours', '30 days', '365.25 days', '10 years'],
          answer: '365.25 days',
          explainer: 'That extra 0.25 day → leap years.',
        },
      },
      {
        heading: 'Why we have seasons',
        body:
          'Earth\u2019s axis is TILTED at 23.5° (relative to the orbital plane). The tilt stays in the SAME direction throughout the year. So at one point in the orbit (June) the Northern Hemisphere is tilted TOWARDS the Sun: long days, high Sun → SUMMER (and the South has winter). Six months later (December) it\u2019s tilted AWAY: short days, low Sun → WINTER (and the South has summer). Spring and autumn are the in-between phases.',
        check: {
          question: 'The seasons are caused by…',
          options: ['Earth getting closer/further from the Sun', 'The tilt of Earth\u2019s axis', 'Volcanoes', 'The Moon'],
          answer: 'The tilt of Earth\u2019s axis',
          explainer: '23.5° tilt → hemispheres alternate which way they face the Sun.',
        },
      },
      {
        heading: 'High Sun vs low Sun',
        body:
          'Tilt has TWO effects in summer: (1) the Sun rises HIGHER in the sky, so its rays hit the ground more directly (more energy per square metre); and (2) the days are LONGER, so there\u2019s more total sunlight. In winter the Sun is low and days short — the same energy is spread over a wider area and a shorter time, so it\u2019s colder. (At the poles, you can have 24 hours of daylight in summer or 24 hours of darkness in winter.)',
        check: {
          question: 'In UK summer, the Sun is…',
          options: ['Lower in the sky and days are shorter', 'Higher in the sky and days are longer', 'Same height, longer days', 'Higher in the sky, shorter days'],
          answer: 'Higher in the sky and days are longer',
          explainer: 'Both factors increase energy received by the ground.',
        },
      },
    ],
    keyPoints: [
      'Earth spins on its axis once every ~24 hours → day/night',
      'Earth orbits the Sun once every 365.25 days → one year',
      'Earth\u2019s axis is tilted at 23.5° — this causes the seasons',
      'Northern summer: N hemisphere tilted TOWARDS the Sun',
      'Higher Sun + longer days = more energy → warmer',
      'When N has summer, S has winter (and vice versa)',
    ],
    workedExamples: [
      {
        problem: 'Explain, with reference to the tilt of Earth\u2019s axis, why the United Kingdom is warmer in June than in December.',
        steps: [
          { step: 1, instruction: 'Tilt direction', working: 'In June, the Northern Hemisphere (which contains the UK) is tilted TOWARDS the Sun.' },
          { step: 2, instruction: 'Sun height', working: 'Sun is high in the sky → light hits the UK more directly → more energy per square metre.' },
          { step: 3, instruction: 'Day length', working: 'Days are longer → more total hours of sunlight.' },
          { step: 4, instruction: 'Result', working: 'More energy received in June → warmer than in December.' },
        ],
        answer: 'In June the UK is tilted towards the Sun; sunlight is more direct AND days are longer, so much more energy is received → warmer than December.',
        commonMistake: 'Saying "Earth is closer to the Sun in summer". Earth\u2019s orbit is nearly circular — distance is NOT the cause of the seasons.',
      },
    ],
    whyItWorks:
      'Two simple motions explain the rhythms of life on Earth: spin (24 h) gives day/night; orbit (1 year) gives the year; tilt of the spin axis turns that yearly journey into four seasons. Get those three pieces and you can explain almost every astronomical pattern you experience.',
  },
  glossary: [
    { term: 'Axis', definition: 'An imaginary line through the North and South poles around which Earth spins.' },
    { term: 'Tilt', definition: 'Earth\u2019s axis is at 23.5° to the line perpendicular to its orbit.' },
    { term: 'Orbit', definition: 'Earth\u2019s path around the Sun, taking 365.25 days.' },
    { term: 'Equinox', definition: 'A day (around 21 March or 23 September) when day and night are roughly equal worldwide.' },
    { term: 'Solstice', definition: 'The longest (June) or shortest (December) day in the Northern Hemisphere.' },
  ],
  examQuestion: {
    prompt:
      '(a) Explain why we experience day and night. (b) Explain why the Northern Hemisphere has summer in June. (c) State whether Earth is closer to the Sun in June. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Earth spins on its axis once every 24 hours',
      'so the side facing the Sun has day, the side facing away has night',
      'Earth\u2019s axis is tilted at 23.5°',
      'In June the Northern Hemisphere is tilted towards the Sun, giving a higher Sun and longer days, so more energy is received',
      'No — Earth\u2019s distance from the Sun barely changes; the seasons are caused by the TILT, not the distance',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-12.3.w1', type: 'choice', question: 'Day and night are caused by Earth…', options: ['Orbiting the Sun', 'Spinning on its axis', 'Wobbling', 'Stopping'], answer: 'Spinning on its axis', hint1: '24-hour cycle', hint2: 'Rotation', hint3: 'Spin once per day' },
      { id: 'phys-12.3.w2', type: 'choice', question: 'One full orbit around the Sun = ___ days.', options: ['1', '30', '365.25', '1000'], answer: '365.25', hint1: 'A year', hint2: 'Includes leap day', hint3: '~365 + 1/4' },
      { id: 'phys-12.3.w3', type: 'text', question: 'Earth\u2019s axis is tilted by ___ degrees.', answer: '23.5', hint1: 'Two digits', hint2: 'Decimal', hint3: 'Twenty-three point five' },
    ],
    level1: [
      { id: 'phys-12.3.1', type: 'choice', question: 'Seasons are caused by…', options: ['Distance to Sun', 'Tilt of Earth\u2019s axis', 'The Moon', 'Wind'], answer: 'Tilt of Earth\u2019s axis', hint1: 'Topic essential', hint2: '23.5° angle', hint3: 'Tilted spin' },
      { id: 'phys-12.3.2', type: 'choice', question: 'When the Northern Hemisphere has summer, the Southern Hemisphere has…', options: ['Summer too', 'Winter', 'Spring', 'No season'], answer: 'Winter', hint1: 'Tilt is one-sided', hint2: 'Opposite seasons', hint3: 'June = north summer' },
    ],
    level2: [
      { id: 'phys-12.3.3', type: 'choice', question: 'Why is the Sun higher in the sky in summer?', options: ['Earth is closer to Sun', 'The tilted hemisphere is angled towards the Sun', 'Sun is brighter', 'Earth slows down'], answer: 'The tilted hemisphere is angled towards the Sun', hint1: 'Tilt effect', hint2: 'Direct sunlight', hint3: 'Geometry of tilt' },
      { id: 'phys-12.3.4', type: 'choice', question: 'Why does summer have more total sunlight?', options: ['Sun shines brighter', 'Days are longer', 'Earth slows down', 'Atmosphere is thinner'], answer: 'Days are longer', hint1: 'Longer daytime', hint2: 'More hours of light', hint3: 'Geometry/tilt result' },
    ],
    level3: [
      { id: 'phys-12.3.5', type: 'choice', question: 'A common misconception about seasons is…', options: ['Earth orbits the Sun', 'Earth is closer to the Sun in summer', 'Earth spins on an axis', 'The axis is tilted'], answer: 'Earth is closer to the Sun in summer', hint1: 'False statement', hint2: 'Distance hardly changes', hint3: 'Real cause is tilt' },
      { id: 'phys-12.3.6', type: 'text', question: 'The longest day of the year in the Northern Hemisphere is the summer ___ .', answer: 'solstice', hint1: '8 letters', hint2: 'Around 21 June', hint3: 'Starts with s' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  GRAVITY_TOPIC,
  SUN_TOPIC,
  DAY_NIGHT_SEASONS_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const earthAndBeyondTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
